/**
 * Export CSV for the Availability grid: the rows as currently filtered, one line
 * per person, name / Home Role / Discipline / Account then Available, Booked, Net
 * and State for each window month. Built in the browser from the cells on screen.
 */
import { monthLabel } from "@/lib/availability/months";
import { STATE_LABEL } from "./labels";
import type { ViewRow } from "./model";

type CsvValue = string | number | null;

/** A leading `=`, `+`, `-`, `@` or control character would run as a formula in a spreadsheet. */
const FORMULA_START = /^[=+\-@\t\r]/;

function escapeField(value: CsvValue): string {
  if (value === null) return "";
  if (typeof value === "number") return String(value);
  const safe = FORMULA_START.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function line(values: readonly CsvValue[]): string {
  return values.map(escapeField).join(",");
}

/**
 * Net is Available − Booked: positive = free hours, negative = hours over.
 * A month the person has no cell for exports blank.
 */
export function buildAvailabilityCsv(rows: readonly ViewRow[], months: readonly string[]): string {
  const header: CsvValue[] = ["Name", "Home Role", "Discipline", "Account"];
  for (const month of months) {
    const label = monthLabel(month);
    header.push(`${label} Available`, `${label} Booked`, `${label} Net`, `${label} State`);
  }

  const lines = [line(header)];
  for (const row of rows) {
    const values: CsvValue[] = [
      row.name,
      row.homeRole,
      row.disciplineCode ?? row.disciplineName,
      row.primaryAccount?.name ?? null,
    ];
    for (const month of months) {
      const cell = row.cells[month];
      if (cell === undefined) {
        values.push(null, null, null, null);
      } else {
        values.push(cell.availableHours, cell.bookedHours, cell.netHours, STATE_LABEL[cell.state]);
      }
    }
    lines.push(line(values));
  }
  return `${lines.join("\r\n")}\r\n`;
}

/** Save text as a file in the browser. */
export function downloadCsv(text: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
